import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles } from "lucide-react";
import MemoireLogo from "@/components/MemoireLogo";

/**
 * "Coming Soon" teaser overlay.
 * Appears 3 minutes (180s) after mount. Dismissible.
 *
 * TO REMOVE: delete this file and remove <ComingSoonOverlay /> from Landing.jsx.
 */
const DELAY_MS = 30 * 1000; // 30 seconds

export default function ComingSoonOverlay() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setOpen(true), DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const close = () => setOpen(false);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="coming-soon-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[100] flex items-center justify-center px-4"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(15,15,16,0.94) 0%, rgba(15,15,16,0.98) 70%)",
            backdropFilter: "blur(12px)",
          }}
          onClick={close}
        >
          {/* Floating gold particles */}
          {[...Array(14)].map((_, i) => (
            <motion.span
              key={i}
              className="pointer-events-none absolute h-1 w-1 rounded-full bg-[#C9A878]"
              initial={{
                x: `${(i * 37) % 100}vw`,
                y: `${(i * 53) % 100}vh`,
                opacity: 0,
              }}
              animate={{
                y: [`${(i * 53) % 100}vh`, `${((i * 53) % 100) - 30}vh`],
                opacity: [0, 0.8, 0],
              }}
              transition={{
                duration: 4 + (i % 5),
                repeat: Infinity,
                delay: i * 0.3,
                ease: "easeInOut",
              }}
            />
          ))}

          {/* Close */}
          <button
            data-testid="coming-soon-close"
            onClick={close}
            className="absolute right-6 top-6 grid h-10 w-10 place-items-center rounded-full border border-white/15 bg-white/5 text-white/70 backdrop-blur-sm transition-all hover:border-white/40 hover:text-white"
            aria-label="Close"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>

          {/* Card */}
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Gold ring behind */}
            <motion.div
              className="pointer-events-none absolute -inset-8 rounded-full"
              style={{
                background:
                  "radial-gradient(circle, rgba(201,168,120,0.18) 0%, rgba(201,168,120,0) 60%)",
              }}
              animate={{ scale: [1, 1.05, 1], opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            />

            <div className="relative flex flex-col items-center gap-8 text-center">
              {/* Logo mark with pulse */}
              <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              >
                <MemoireLogo variant="mark" gold="#C9A878" size={72} />
              </motion.div>

              {/* Chip */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="inline-flex items-center gap-2 rounded-full border border-[#C9A878]/30 bg-[#C9A878]/10 px-4 py-1.5"
              >
                <Sparkles className="h-3 w-3 text-[#C9A878]" strokeWidth={1.5} />
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#C9A878]">
                  Segera Hadir
                </span>
              </motion.div>

              {/* Headline with letter animation */}
              <div className="overflow-hidden">
                <motion.h2
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="font-['Playfair_Display'] text-5xl italic tracking-tight text-white sm:text-6xl lg:text-7xl"
                >
                  Coming Soon
                </motion.h2>
              </div>

              {/* Animated divider */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 1, delay: 1.1, ease: "easeInOut" }}
                className="h-px w-32 origin-center bg-gradient-to-r from-transparent via-[#C9A878] to-transparent"
              />

              {/* Sub */}
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 1.3 }}
                className="max-w-md text-sm leading-relaxed text-neutral-300 sm:text-base"
              >
                Kami sedang menyempurnakan setiap detail. <br />
                Aplikasi MEMOIRE akan segera dirilis untuk mengabadikan
                cerita Anda — dari setiap sudut.
              </motion.p>

              {/* CTAs */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 1.5 }}
                className="flex flex-col items-center gap-3 sm:flex-row"
              >
                <a
                  href="https://wa.me/6287881161127?text=Halo%20MEMOIRE!%20Saya%20ingin%20info%20lebih%20lanjut."
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="coming-soon-wa-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-[#C9A878] px-6 py-3 text-sm font-medium text-neutral-950 transition-all hover:bg-[#dcbb8b]"
                >
                  Hubungi Kami
                </a>
                <button
                  onClick={close}
                  data-testid="coming-soon-dismiss-btn"
                  className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 transition-colors hover:text-white"
                >
                  Lanjut melihat website
                </button>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
